<template>
  <div
    v-if="!isPlacementVisible"
    v-observe-visibility="{ callback: placementVisibility, once: true}">
    <slot name="skeleton"></slot>
  </div>
  <div v-else-if="!dataForRender.length && isLoading">
    <slot name="skeleton"></slot>
  </div>
  <div
    v-else
    class="page-container page-container--rendered-blocks rendered-blocks">
    <div
      v-for="item in dataForRender"
      class="rendered-blocks__item"
      :class="{'rendered-blocks__item--without-margin': withoutTopMargin}"
      :id="item.anchor"
      :key="item.id">
      <products-block
        v-if="Object.values(PRODUCTS_BLOCKS_TYPES).includes(item.type)"
        :data="item"
        :product-sku="productSku"
        :category-key="categoryKey"
        :without-container="withoutContainer"
        :is-product-page-under-price="isProductPageUnderPrice"
        :is-cabinet="isCabinet"
      ></products-block>
      <categories-with-products
        v-else-if="item.type === BLOCKS_TYPES.CATEGORIES_WITH_PRODUCTS"
        :data="item"
      ></categories-with-products>
      <our-features-block
        v-else-if="item.type === BLOCKS_TYPES.OUR_FEATURES"
        :data="item"
      ></our-features-block>
      <product-of-the-day
        v-else-if="item.type === BLOCKS_TYPES.PRODUCT_OF_THE_DAY"
        :data="item"
        :refetch-action="getBlocksInfo"
      ></product-of-the-day>
      <top-categories-block
        v-else-if="item.type === BLOCKS_TYPES.TOP_CATEGORIES"
        :data="item"
      ></top-categories-block>
      <youtube-block
        v-else-if="item.type === BLOCKS_TYPES.YOUTUBE"
        :data="item"
      ></youtube-block>
      <products-with-video
        v-else-if="item.type === BLOCKS_TYPES.VIDEO_PRODUCTS"
        :items="item.data"
        :title="computedTitle(item)"
        :title-ru="item.title_ru"
        :id="computedTitle(item)"
      ></products-with-video>
      <catalog-slider
        v-else-if="item.type === BLOCKS_TYPES.CATALOG"
        :catalog="item.data"
        :title="computedTitle(item)"
        :title-ru="item.title_ru"
        :link="item.link"
        :id="computedTitle(item)"
      ></catalog-slider>
      <articles-slider
        v-else-if="item.type === BLOCKS_TYPES.ARTICLES"
        :articles="item.data"
        :title="computedTitle(item)"
        :title-ru="item.title_ru"
        :link="item.link"
        :id="computedTitle(item)"
      ></articles-slider>
      <text-block
        v-else-if="item.type === BLOCKS_TYPES.TEXT"
        :html="isUkLanguage ? item.settings.text_uk : item.settings.text_ru"
        :title="computedTitle(item)"
        :title-ru="item.title_ru"
        :id="computedTitle(item)"
      ></text-block>
      <photo-block
        v-else-if="item.type === BLOCKS_TYPES.IMAGE"
        :src-set="item.data"
        :alt="isUkLanguage ? item.settings.alt_uk : item.settings.alt_ru"
        :title="computedTitle(item)"
        :id="computedTitle(item)"
        :without-container="withoutContainer"
      ></photo-block>
      <countdown-product
        v-else-if="item.type === BLOCKS_TYPES.COUNTERS && (item.counter_data.placement_type === 'product_page' || item.counter_data.placement_type === 'product_page_small')"
        :initial-data="item.counter_data"
      ></countdown-product>
      <countdown-page
        v-else-if="item.type === BLOCKS_TYPES.COUNTERS && item.counter_data.placement_type.includes('sale_page')"
        :initial-data="item.counter_data"
      ></countdown-page>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { BLOCKS_TYPES, PRODUCTS_BLOCKS_TYPES } from '@/constants/blocks'
import { mapActions, mapGetters } from 'vuex'
import CatalogSlider from './catalog/CatalogSlider'
import ArticlesSlider from './articles/ArticlesSlider'
import TextBlock from './TextBlock'
import TopCategoriesBlock from './TopCategories'
import OurFeaturesBlock from './OurFeaturesBlock'
import CategoriesWithProducts from '@/components/rendered-blocks/CategoriesWithProducts.vue'
import ProductOfTheDay from '@/components/rendered-blocks/ProductOfTheDay.vue'
import PhotoBlock from './PhotoBlock'
import ProductsBlock from '../product/product-block/ProductsBlock'
import ProductsWithVideo from '../product/product-block/ProductsWithVideo'
import YoutubeBlock from './youtube/YoutubeBlock'
import CountdownProduct from '@/components/rendered-blocks/countdown/CountdownProduct.vue'
import CountdownPage from '@/components/rendered-blocks/countdown/CountdownPage.vue'
// import debounce from 'lodash/debounce'

export default {
  name: 'RenderedBlocks',
  components: {
    CountdownProduct,
    CountdownPage,
    YoutubeBlock,
    ProductsWithVideo,
    ProductsBlock,
    CatalogSlider,
    ArticlesSlider,
    TextBlock,
    PhotoBlock,
    TopCategoriesBlock,
    OurFeaturesBlock,
    ProductOfTheDay,
    CategoriesWithProducts
  },
  data () {
    return {
      BLOCKS_TYPES: BLOCKS_TYPES,
      PRODUCTS_BLOCKS_TYPES: PRODUCTS_BLOCKS_TYPES,
      isPlacementVisible: false,
      blocksInfo: [],
      isLoading: false,
      isHash: false,
      hashBlock: null,
      windowWidth: window.innerWidth
    }
  },
  pageInstances: [],
  attempts: [],
  props: {
    uid: [String, Number],
    productId: [String, Number],
    categoryId: [String, Number],
    productSku: [String, Number],
    categoryKey: [String, Number],
    data: {
      type: Array
    },
    withoutContainer: {
      type: Boolean,
      default: false
    },
    withoutTopMargin: {
      type: Boolean,
      default: false
    },
    isProductPageUnderPrice: {
      type: Boolean,
      default: false
    },
    isCabinet: {
      type: Boolean,
      default: false
    }
  },
  updated () {
    this.parseHash()
    if (this.isHash && this.dataForRender === this.hashBlock) {
      const anchor = document.getElementById(this.hash.substring(1))
      const header = document.getElementsByClassName('page-header__main')
      if (anchor) {
        let y = anchor.getBoundingClientRect().top + window.pageYOffset
        if (this.isMobile && !header[0]?.classList.contains('header-mobile--fixed')) {
          y -= 70
        }
        this.SET_INFINITE_SCROLL_TRIGGER_STATUS(true)
        window.scrollTo({ top: y, behavior: 'smooth' })
        setTimeout(() => {
          this.SET_INFINITE_SCROLL_TRIGGER_STATUS(false)
        }, 3000)
      }
      this.isHash = false
    }
  },
  computed: {
    ...mapGetters(['urlLanguagePrefix', 'isUkLanguage']),
    dataForRender () {
      return this.data ? this.data : this.blocksInfo
    },
    isMobile () {
      return this.windowWidth < 1024
    }
  },
  mounted () {
    this.parseHash()
    window.addEventListener('load', () => this.processLoad())
  },
  methods: {
    ...mapActions([
      'SET_INFINITE_SCROLL_TRIGGER_STATUS'
    ]),
    placementVisibility (isVisible) {
      this.isPlacementVisible = isVisible
    },
    getBlocksInfo () {
      this.isLoading = true

      api.blocks.getBlocks({ uid: this.uid, productId: this.productId, categoryId: this.categoryId })
        .then(res => {
          if (res) {
            this.blocksInfo = res

            const placementDetail = {
              blocksInfo: this.blocksInfo,
              placementUid: this.uid
            }

            window.dispatchEvent(new CustomEvent('placementReady', { detail: placementDetail }))
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    computedTitle (item) {
      return item.show_title === 'Y' ? (this.isUkLanguage ? item.title_uk : item.title_ru) : ''
    },
    saveInstances (uid) {
      if (this.$options.pageInstances.indexOf(uid) === -1) this.$options.pageInstances.push(uid)
    },
    processBlock (res) {
      if (res && res.length > 0 &&
        res.find(i => i.anchor === this.hash.substring(1))) {
        this.isPlacementVisible = true
        this.hashBlock = this.blocksInfo = res
      } else {
        if (this.$options.attempts.indexOf(this.uid) === -1) this.$options.attempts.push(this.uid)
        if (this.$options.attempts.length === this.$options.pageInstances.length) {
          window.scrollTo(0, 0)
          this.$options.attempts = []
        }
      }
      this.isLoading = false
    },
    async getHashBlock () {
      await api.blocks.getBlocks({ uid: this.uid, productId: this.productId, categoryId: this.categoryId })
        .then(res => this.processBlock(res))
    },
    processLoad () {
      this.parseHash()
      if (window.location.hash) {
        this.getHashBlock()
      }
    },
    parseHash () {
      if (window.location.hash) {
        this.hash = decodeURI(window.location.hash)
        this.isHash = true
      }
    }
  },
  watch: {
    isPlacementVisible: {
      immediate: true,
      handler (value) {
        if (value) {
          !this.data && this.uid && this.getBlocksInfo()
        }
      }
    },
    '$route.hash': function (newVal, oldVal) {
      this.saveInstances(this.uid)

      if (!this.dataForRender.find(i => i.anchor === newVal.substring(1))) {
        this.isHash = true
        this.getHashBlock()
      }
    }
  }
}
</script>

<style lang="sass">
.rendered-blocks__item
  &:has(*)
    margin-top: 30px
    +mmin($tablet)
      margin-top: 60px
  &--without-margin
    margin-top: 0
    +mmin($tablet)
      margin-top: 0
</style>
